<template>
	<div class="content-block">
		<h2>Delete {{ route.params.itemType }}: {{ itemLabel }}</h2>
	</div>
	
	<div v-if="deleteSuccess !== false" class="content-block dx-card">
		
	</div>

	<div :class="{ 'content-block': true, 'dx-card': true, 'delete-item-confirm': true, 'delete-item-error': true }">
		<DxLoadPanel :visible="(fatalError === null && loadedItem === null) || isDeleting" container=".delete-item-confirm" :position="{ of: '.delete-item-confirm' }" :show-indicator="true" :show-pane="true" shading-color="rgba(0,0,0,0.4)" />
		<div class="dx-error-message" v-if="fatalError !== null">
			<i class="dx-icon-warning"></i><div class="dx-error-message-text">{{ fatalError }}</div>
		</div>
		<div class="dx-success-message" v-if="deleteSuccess !== false">
			<i class="dx-icon-check"></i><div class="dx-success-message-text">{{ deleteSuccess }}</div>
		</div>
		<div style="padding: 24px; display: flex; flex-direction: column; gap: 12px;" v-if="fatalError === null && deleteSuccess === false && loadedItem !== null">
			<div style="display: flex; gap: 12px;">
				<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
				<div>
					Are you sure you want to delete the {{ route.params.itemType }} <b>{{ itemLabel }}</b>?<br />
					<i>This action cannot be undone.</i><br />
				</div>
			</div>
			<div v-if="route.params.itemType == 'page'">
				All components on this page (lists, charts, etc.) will be lost.<br>
				All data created on this page (content, uploads, etc.) will still be kept.
			</div>
			<div v-else-if="route.params.itemType == 'group'">
				All pages in this group will be automatically deleted.<br>
				All data created in these pages or group will still be kept.
			</div>
			<div style="display: flex; gap: 12px; justify-content: flex-end;">
				<DxButton type="info" text="Cancel" @click="cancelButton" />
				<DxButton type="danger" text="Delete" @click="deleteButton" />
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.delete-item-confirm {
	max-width: 720px;
	min-height: 200px;
	&.delete-item-error {
		min-height: initial;
	}
}
</style>

<script setup>
/* eslint-disable */
import { ref, inject, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxButton } from 'devextreme-vue/button';
import GroupCache from '@/groups';

// TODO: Implement this for non-quick deletion cases for fields and schemas. These are applied whenever a delete is requested
// with these, and data exists for either.

// We use item-delete to handle deletion for large items that need special confirmation, e.g. pages, groups, etc.
const route = useRoute();
const router = useRouter();
const $http = inject('$http');

const fatalError = ref(null);						// Fatal error message to disable form.
const loadedItem = ref(null);						// Loaded item data to display in preperation for deletion.
const isDeleting = ref(false);						// If we are in processs of deleting item via DELETE call.
const deleteSuccess = ref(false);					// False until delete successfully completes, otherwise string message to display.

const allowedTypes = [ 'page', 'group' ];			// TODO: Add in other types as needed, e.g. user/role/etc.
const itemLabel = computed(() => {
	if (!loadedItem.value)
		return "";
	if (loadedItem.value.title)
		return loadedItem.value.title;
	return `#${loadedItem.value.id}`;
});

// Watch for route changes (and also immediately once mounted), to populate our warnings and prompt for deletion.
watch( () => [route.params],
	() => {
		console.log("Initialize for route params:", route.params);
		loadedItem.value = null;
		fatalError.value = null;
		// Fetch latest version of this item to ensure we have permissions and up-to-date data.
		if (!allowedTypes.includes(route.params.itemType)) {
			fatalError.value = `Unknown or invalid item type ${route.params.itemType}.`;
			return;
		}
		$http.get(`${route.params.itemType}/${route.params.itemId}`).then((response) => {
			// TODO: Check if we are allowed to delete this item; permissions check remotely?
			loadedItem.value = response.data;
		}).catch((error) => {
			console.error("Error fetching item data for deletion:", error);
			if (error.response && error.response.status == 404) {
				fatalError.value = error.message + ". Check the item has not already been deleted.";
			} else {
				fatalError.value = error.message + ". Your " + route.params.itemType + " has not been deleted.";
			}
		});
	},
	{ immediate: true }
);

// When the user clicks cancel, we try to go back, except if our 'back' is another 'delete' page -- which might be dangerous.
// In this case, we just go back to the home page.
function cancelButton() {
	const previousRoute = router.options.history.state.back;
	if (!previousRoute || previousRoute.startsWith('delete') || previousRoute.startsWith('/delete')) {
		router.push('/');
	} else {
		router.back();
	}
}

// On deletion, show a loading indicator and then replace the componetn with a success message, reloading the group list.
function deleteButton() {
	isDeleting.value = true;
	$http.delete(`${route.params.itemType}/${route.params.itemId}`).then((response) => {
		deleteSuccess.value = response.data && response.data.message ? response.data.message : `The ${route.params.itemType} has been deleted successfully.`;
		GroupCache.fetchGroupList();
	}).catch((error) => {
		fatalError.value = "Error deleting this item. " + error.message;
		console.error("Error deleting item", error);
	}).finally(() => {
		isDeleting.value = false;
	});
}	

</script>